import React, { Fragment, useContext, useEffect } from "react";
import {
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
} from "@mui/material";
import CashMovement from "./CashMovement";
import { Input } from "controls";
import { AppContext } from "App";
import { action, API, controller } from "api";
import { useFormatPrice } from "hooks";

const Payments = ({
  activeReg,
  txns,
  values,
  setValues,
  errors,
  setErrors,
  setTotals,
  totals,
}) => {
  const formatPrice = useFormatPrice();
  const { setLoader, authUser } = useContext(AppContext);

  useEffect(() => {
    if (authUser && activeReg && activeReg.id && activeReg.status !== 2) {
      setLoader(true);
      API(controller.SellReg + action.RegSellWithPayType).getById(
        activeReg.id,
        (res) => {
          if (res && res.status === 200) {
            var _payTypes = res.result;
            // Set Register Values
            setValues({
              ...activeReg,
              cashMovement: res.cashMovement || 0,
              sellRegPayments: _payTypes.map((j) => ({
                sellRegId: activeReg.id,
                payTypeId: j.id,
                expected: j.sellAmount,
                counted: "",
                differences: "",
                payTypeName: j.name,
              })),
            });
          }
          setLoader(false);
        }
      );
    } else setValues({ ...activeReg });

    return () => {};
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [authUser, activeReg]);

  useEffect(() => {
    setTotals({
      expected:
        values.cashMovement +
        values.sellRegPayments
          .map((j) => j.expected)
          .reduce((x, y) => x + y, 0),

      counted: values.sellRegPayments
        .map((j) => parseFloat(j.counted || 0))
        .reduce((x, y) => x + y, 0),

      differences: values.sellRegPayments
        .map((j) => j.differences || 0)
        .reduce((x, y) => x + y, 0),
    });
    return () => {};
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [values.sellRegPayments]);

  const handleChgCounted = (payTypeId, val) => {
    setValues((j) => ({
      ...j,
      sellRegPayments: j.sellRegPayments.map((x) =>
        x.payTypeId === payTypeId
          ? {
              ...x,
              counted: val,
              differences:
                parseFloat(val) -
                  (x.payTypeId === 1 && x.payTypeName === "Cash"
                    ? parseFloat(x.expected) + values.cashMovement
                    : parseFloat(x.expected)) || "",
            }
          : x
      ),
    }));
    setErrors((j) => ({ ...j, [payTypeId + "counted"]: "" }));
  };

  return (
    <>
      <div className="bg-white rounded-[15px]">
        <div className="pb-[10px] p-[15px] border-b">
          <span className="text-[18px] font-medium text-gray-500 tracking-wide border-left">
            {activeReg.status === 2 && "Last"} Payments Summary
          </span>
        </div>
        <div className="mt-[20px]">
          <TableContainer component={Paper}>
            <Table sx={{ minWidth: 650 }} aria-label="simple table">
              <TableHead>
                <TableRow className="uppercase !tracking-wider">
                  <TableCell className="primary-color !font-semibold !tracking-wider">
                    Payment Method
                  </TableCell>
                  <TableCell
                    align="right"
                    className="primary-color !font-semibold !tracking-wider"
                  >
                    Expected
                  </TableCell>
                  <TableCell
                    align="right"
                    className="primary-color !font-semibold !tracking-wider"
                  >
                    Counted
                  </TableCell>
                  <TableCell
                    align="right"
                    className="primary-color !font-semibold !tracking-wider"
                  >
                    Differences
                  </TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {values.sellRegPayments &&
                  values.sellRegPayments.map((payment, ind) => (
                    <Fragment key={ind}>
                      <TableRow
                        className={ind === 0 ? "border-left bg-slate-50" : ""}
                      >
                        <TableCell>
                          <div className="grid">
                            <span className="text-[18px] text-gray-500">
                              {payment.payTypeName}
                            </span>
                            {payment.payTypeName === "Cash" &&
                              payment.payTypeId === 1 && (
                                <span className="text-[12px] primary-color tracking-wide font-medium">
                                  Cash Payments, Floats and Movements
                                </span>
                              )}
                          </div>
                        </TableCell>
                        <TableCell
                          align="right"
                          className="!text-[18px] !text-gray-500"
                        >
                          {formatPrice(
                            ind === 0
                              ? payment.expected + values.cashMovement
                              : payment.expected
                          )}
                        </TableCell>
                        <TableCell align="right">
                          {activeReg.status === 2 ? (
                            <span className="!text-[18px] !text-gray-500">
                              {formatPrice(payment.counted)}
                            </span>
                          ) : (
                            <Input
                              variant="outlined"
                              size="small"
                              sx={{
                                maxWidth: "150px",
                                "& .MuiInputBase-input": { textAlign: "right" },
                              }}
                              placeholder="Enter Amount"
                              error={errors[payment.payTypeId + "counted"]}
                              onBlur={(e) =>
                                handleChgCounted(
                                  payment.payTypeId,
                                  e.target.value
                                )
                              }
                            />
                          )}
                        </TableCell>
                        <TableCell
                          align="right"
                          className={`!text-[18px] ${
                            payment.differences > 0
                              ? "!text-green-500"
                              : !payment.differences
                              ? "primary-color"
                              : "!text-red-500"
                          }`}
                        >
                          {formatPrice(payment.differences) || "-"}
                        </TableCell>
                      </TableRow>

                      {/* Cash Movements */}
                      {ind === 0 && (
                        <CashMovement
                          {...{ txns, cashReceived: payment.expected }}
                        />
                      )}
                    </Fragment>
                  ))}

                {/* ==== Total ==== */}
                <TableRow>
                  <TableCell className="border-t !p-[10px]">
                    <span className="text-[18px] uppercase primary-color font-medium pl-[10px]">
                      Total
                    </span>
                  </TableCell>
                  <TableCell align="right" className="!p-[10px]">
                    <span className="text-[18px] text-gray-500 font-medium">
                      {formatPrice(totals.expected)}
                    </span>
                  </TableCell>
                  <TableCell align="right" className="!p-[10px]">
                    <span className="text-[18px] text-gray-500 font-medium">
                      {formatPrice(totals.counted)}
                    </span>
                  </TableCell>
                  <TableCell align="right" className="!p-[10px]">
                    <span className="text-[18px] text-gray-500 font-medium">
                      {formatPrice(totals.differences)}
                    </span>
                  </TableCell>
                </TableRow>
              </TableBody>
            </Table>
          </TableContainer>
        </div>
      </div>
    </>
  );
};

export default Payments;
